import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'sonner';
import { TrendingUp, Users, Trophy, Target, Flame, ArrowRight, BarChart3, Vote, Sparkles, Activity, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts';
import MarketCard from '../components/shared/MarketCard';
import BoostSpotlight from '../components/shared/BoostSpotlight';
import { isMarketBoosted } from '../components/shared';
import CivicMarketCard from '../components/shared/CivicMarketCard';
import CreditPurchaseDialog from '../components/shared/CreditPurchaseDialog';
import { mixMarketList } from '../utils';

const TrendingCard = ({ market, index }) => {
  const navigate = useNavigate();
  const isCivic = market.mode === 'civic';
  const isPrediction = market.mode === 'prediction' || !market.mode;

  return (
    <div
      className="group relative bg-white dark:bg-slate-900/80 dark:backdrop-blur-sm rounded-xl border border-gray-100 dark:border-slate-800 p-4 hover:shadow-lg dark:hover:shadow-black/40 hover:border-emerald-200 dark:hover:border-emerald-500/40 transition-all duration-300 cursor-pointer dark:hover:border-emerald-500/30"
      onClick={() => navigate(`/markets/${market.id}`)}
      data-testid={`trending-card-${index}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge
              variant="secondary"
              className={`text-[9px] font-bold px-1.5 py-0 ${
                isCivic
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300'
                  : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
              }`}
            >
              {isCivic ? 'Civic' : 'Prediction'}
            </Badge>
            <Badge variant="outline" className="text-[9px] px-1.5 py-0 dark:border-slate-700 dark:text-gray-400">
              {market.category || market.civic_category || 'General'}
            </Badge>
          </div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight line-clamp-2 group-hover:text-emerald-700 dark:group-hover:text-emerald-400 transition-colors">
            {market.title}
          </h4>
        </div>
        {market.image_url && (
          <div className="flex-shrink-0 w-16 h-12 rounded-md overflow-hidden bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700">
            <img
              src={market.image_url}
              alt={market.title}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
            />
          </div>
        )}
      </div>

      {/* Activity metrics */}
      <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-50 dark:border-slate-800">
        {market.recent_bets > 0 && (
          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <BarChart3 size={12} className="text-emerald-500" />
            <span className="font-medium">{market.recent_bets}</span> Wetten
          </div>
        )}
        {market.recent_votes > 0 && (
          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <Vote size={12} className="text-blue-500" />
            <span className="font-medium">{market.recent_votes}</span> Stimmen
          </div>
        )}
        {market.recent_volume > 0 && (
          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <TrendingUp size={12} className="text-amber-500" />
            <span className="font-medium">{Math.round(market.recent_volume)}</span> VC Vol.
          </div>
        )}
        {market.recent_bets === 0 && market.recent_votes === 0 && (
          <div className="text-xs text-gray-400 dark:text-gray-500">Neu erstellt</div>
        )}
      </div>
    </div>
  );
};

// Dekorative Mock-Market-Card fuer den Landing-Hero. Bewusst NICHT
// klickbar / nicht interaktiv -- der einzige Zweck ist visuell zu
// zeigen, wie ein Market in der App aussieht. Probabilities, Volume
// und Trend sind hartkodiert. aria-hidden auf dem Stack im Parent
// haelt das fuer Screen-Reader unsichtbar.
const HeroPreviewCard = ({ category, title, probability, trend, volumeVc, liveBets, posStyle, rotate, z }) => {
  const isPositive = trend.startsWith('+');
  return (
    <div
      className="absolute w-[300px] sm:w-[340px] transition-transform duration-300 hover:-translate-y-1"
      style={{ ...posStyle, transform: `rotate(${rotate}deg)`, zIndex: z }}
    >
      <div className="rounded-xl border border-gray-100 bg-white dark:border-slate-800 dark:bg-slate-900/80 dark:backdrop-blur-sm shadow-xl shadow-blue-900/5 dark:shadow-black/40 p-4">
        <div className="flex items-center justify-between mb-3">
          <Badge variant="secondary" className="text-[10px] font-bold px-2 py-0.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 dark:text-emerald-400">
            {category}
          </Badge>
          <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
            Live
          </span>
        </div>
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-snug line-clamp-2 mb-3">
          {title}
        </p>
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">YES</div>
            <div className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-blue-900 dark:from-blue-200 dark:to-white bg-clip-text text-transparent dark:text-blue-300">{probability}%</div>
          </div>
          <div className={`text-xs font-semibold ${isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
            {trend}
          </div>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-slate-800">
          <div
            className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600"
            style={{ width: `${probability}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-3 text-[11px] text-gray-500 dark:text-gray-400">
          <span className="inline-flex items-center gap-1">
            <Activity className="h-3 w-3 text-emerald-500" />
            {liveBets} Wetten
          </span>
          <span>{volumeVc.toLocaleString('de-DE')} VC Vol.</span>
        </div>
      </div>
    </div>
  );
};

const HomePage = () => {
  const [markets, setMarkets] = useState([]);
  const [civicMarkets, setCivicMarkets] = useState([]);
  const [civicResults, setCivicResults] = useState({});
  const [trending, setTrending] = useState([]);
  const [marketOutcomes, setMarketOutcomes] = useState({});
  const [categories, setCategories] = useState(['all']);
  const [activeStatus, setActiveStatus] = useState('open');
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const { user, isAdmin, refreshCredits } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [marketsRes, civicRes, trendingRes, civicResultsRes] = await Promise.all([
        api.markets.getAll(),
        api.civic.getMarkets(),
        api.markets.trending(),
        api.civic.getResultsBulk().catch(() => ({ data: {} })),
      ]);

      setMarkets(marketsRes.data);
      setCivicMarkets(civicRes.data);
      setTrending(trendingRes.data);
      setCivicResults(civicResultsRes.data || {});

      // Build unified categories
      const predCats = marketsRes.data.map(m => m.category);
      const civicCats = civicRes.data.map(m => m.civic_category).filter(Boolean);
      const uniqueCategories = ['all', ...new Set([...predCats, ...civicCats])];
      setCategories(uniqueCategories);

      try {
        const outcomesRes = await api.outcomes.getBulk();
        setMarketOutcomes(outcomesRes.data);
      } catch {}
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseSuccess = () => {
    if (refreshCredits) refreshCredits();
  };

  const handlePredictNowClick = () => {
    navigate(user ? '/markets' : '/login');
  };

  const refreshMarketOutcomes = async (marketId) => {
    try {
      const res = await api.markets.getOutcomes(marketId);
      setMarketOutcomes(prev => ({ ...prev, [marketId]: res.data }));
    } catch {}
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 dark:from-black dark:via-blue-950 dark:to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Target className="h-12 w-12 text-emerald-600 mx-auto animate-spin dark:text-emerald-400" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Lade Märkte...</p>
        </div>
      </div>
    );
  }

  return (
    // -mt-[61px] zieht den Wrapper unter den fixed-Header hoch, sodass
    // der Gradient unter dem Header durchlaeuft (backdrop-blur des Headers
    // blurrt dann den Gradient statt body-bg). pt-[61px] gibt das
    // entfernte Spacing innen zurueck, damit Content nicht hinter dem
    // Header verschwindet.
    <div className="min-h-screen -mt-[61px] pt-[61px] bg-gradient-to-br from-sky-50 to-blue-50 dark:from-black dark:via-blue-950 dark:to-slate-950">
      {/* Hero — zweispaltig ab lg: links der Pitch + CTA, rechts ein
          Live-Preview-Stack aus Mock-Markets. Anonymous bekommt den
          Sign-up-Pitch + Trust-Strip, Logged-in den persoenlichen
          Schnellzugriff. Hintergrund-Glow rechts wird ueber radial-
          gradient + blur erzeugt, ohne SVG-Assets. */}
      <section className="relative overflow-hidden py-12 sm:py-20">
        {/* Decorative glow */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-24 right-[-10%] hidden lg:block"
        >
          <div className="h-[560px] w-[560px] rounded-full bg-emerald-200/40 dark:bg-blue-500/20 blur-3xl" />
        </div>

        <div className="container relative mx-auto px-4">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            {/* Linke Spalte — Pitch + CTA */}
            <div className="text-center lg:text-left">
              {user ? (
                <>
                  <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 sm:mb-6 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200 text-xs sm:text-sm dark:border-emerald-500/30">
                    <Sparkles className="h-3.5 w-3.5" />
                    <span>Eingeloggt als {user.handle}</span>
                  </div>
                  <h1 className="text-3xl sm:text-5xl font-bold bg-gradient-to-r from-blue-700 to-blue-900 dark:from-blue-200 dark:to-white bg-clip-text text-transparent mb-4 sm:mb-6">
                    Willkommen zurück, {user.handle}
                  </h1>
                  <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 mb-6 sm:mb-8 max-w-xl mx-auto lg:mx-0 px-4 lg:px-0 dark:text-gray-400">
                    {isAdmin
                      ? 'Schau dir die Markets an oder verwalte die Plattform im Admin-Panel.'
                      : 'Schau dir die heißesten Märkte an oder springe direkt in dein Portfolio.'}
                  </p>
                  <div className="flex items-center justify-center lg:justify-start gap-3 flex-wrap">
                    <Button
                      size="lg"
                      className="text-base sm:text-lg px-6 sm:px-8 py-2 sm:py-3 shadow-md hover:shadow-lg transition-shadow"
                      onClick={() => navigate('/markets')}
                    >
                      Zu den Markets <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      className="text-base sm:text-lg px-6 sm:px-8 py-2 sm:py-3"
                      onClick={() => navigate(isAdmin ? '/admin' : '/portfolio')}
                    >
                      {isAdmin ? 'Admin-Panel' : 'Mein Portfolio'}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 sm:mb-6 rounded-full border border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-500/40 dark:bg-orange-500/10 dark:text-orange-300 text-xs sm:text-sm">
                    <span>🧪</span>
                    <span>Showcase-Beta · kostenlos testen</span>
                  </div>
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight bg-gradient-to-r from-blue-700 to-blue-900 dark:from-blue-200 dark:to-white bg-clip-text text-transparent mb-4 sm:mb-6 !leading-[1.15] pb-1">
                    Vorhersagen, die zählen.
                  </h1>
                  <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 mb-6 sm:mb-8 max-w-xl mx-auto lg:mx-0 px-4 lg:px-0 leading-relaxed dark:text-gray-400">
                    Politik, Sport, Tech – tippe auf das, was als nächstes passiert,
                    mit virtuellen Credits. Keine Echtgeld-Einsätze, keine
                    Zahlungsdaten. <span className="font-semibold text-emerald-700 dark:text-emerald-400">+1.000 Credits</span> Startguthaben + täglicher Bonus.
                  </p>
                  <div className="flex items-center justify-center lg:justify-start gap-3 flex-wrap mb-6">
                    <Button
                      size="lg"
                      className="text-base sm:text-lg px-6 sm:px-8 py-2 sm:py-3 shadow-md hover:shadow-lg transition-shadow"
                      onClick={handlePredictNowClick}
                    >
                      Kostenlos starten <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      className="text-base sm:text-lg px-6 sm:px-8 py-2 sm:py-3"
                      onClick={() => navigate('/markets')}
                    >
                      Märkte ansehen
                    </Button>
                  </div>
                  {/* F1: Creator-Einstieg auch ausgeloggt sichtbar */}
                  <button
                    type="button"
                    onClick={() => navigate('/creator')}
                    className="inline-flex items-center gap-1.5 mb-6 px-3 py-1.5 rounded-full border border-orange-300 dark:border-orange-500/40 bg-orange-50 dark:bg-orange-500/10 text-sm font-medium text-orange-600 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-500/20 transition-colors"
                  >
                    <Sparkles className="h-4 w-4" /> Eigene Märkte erstellen &amp; mitverdienen — Creator werden
                  </button>
                  <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 text-xs">
                    <span className="inline-flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400">
                      <ShieldCheck className="h-3.5 w-3.5" /> Keine Kreditkarte nötig
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400">
                      <ShieldCheck className="h-3.5 w-3.5" /> +25 VC pro Tag
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400">
                      <ShieldCheck className="h-3.5 w-3.5" /> +100 VC pro Empfehlung
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Rechte Spalte — Live-Preview-Stack. Auf mobile versteckt,
                ab lg eingeblendet. Zeigt drei gefakte Markets in den
                Kategorien Politik/Sport/Tech mit YES-Probability,
                Live-Pulse und einem Pseudo-Preisverlauf. Anchor fuer den
                Pitch (zeigt sofort was die Plattform ist). Parent hat
                feste Hoehe damit absolute-positionierte Kinder nicht den
                Grid-Zellen-Collapse triggern. */}
            <div className="hidden lg:block">
              <div className="relative mx-auto h-[480px] w-full max-w-[420px]" aria-hidden="true">
                <HeroPreviewCard
                  category="Tech"
                  title="GPT-6 launcht vor Q3 2027"
                  probability={78}
                  trend="+12.4%"
                  volumeVc={9610}
                  liveBets={412}
                  posStyle={{ top: 0, left: 0 }}
                  rotate={-3}
                  z={10}
                />
                <HeroPreviewCard
                  category="Sport"
                  title="BVB schlägt Bayern im DFB-Pokal-Halbfinale"
                  probability={37}
                  trend="-1.8%"
                  volumeVc={2150}
                  liveBets={94}
                  posStyle={{ top: 100, left: 60 }}
                  rotate={2}
                  z={20}
                />
                <HeroPreviewCard
                  category="Politik"
                  title="Bundestagswahl 2029 — SPD über 18%?"
                  probability={62}
                  trend="+4.1%"
                  volumeVc={4820}
                  liveBets={183}
                  posStyle={{ top: 220, left: 20 }}
                  rotate={-1.5}
                  z={30}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 sm:py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8">
            <Card className="text-center dark:bg-slate-900/80 dark:border-slate-800 dark:backdrop-blur-sm dark:shadow-xl dark:shadow-black/40">
              <CardContent className="pt-4 sm:pt-6 px-2 sm:px-6">
                <TrendingUp className="h-8 w-8 sm:h-12 sm:w-12 text-emerald-600 mx-auto mb-2 sm:mb-4 dark:text-emerald-400" />
                <h3 className="text-lg sm:text-2xl font-bold mb-1 sm:mb-2">Live Markets</h3>
                <p className="text-xs sm:text-base text-gray-600 dark:text-gray-400">{markets.length} aktiv</p>
              </CardContent>
            </Card>
            <Card className="text-center dark:bg-slate-900/80 dark:border-slate-800 dark:backdrop-blur-sm dark:shadow-xl dark:shadow-black/40">
              <CardContent className="pt-4 sm:pt-6 px-2 sm:px-6">
                <Users className="h-8 w-8 sm:h-12 sm:w-12 text-emerald-600 mx-auto mb-2 sm:mb-4 dark:text-emerald-400" />
                <h3 className="text-lg sm:text-2xl font-bold mb-1 sm:mb-2">Community</h3>
                <p className="text-xs sm:text-base text-gray-600 dark:text-gray-400">Folge Freunden und vergleicht eure Performance</p>
              </CardContent>
            </Card>
            <Card className="text-center dark:bg-slate-900/80 dark:border-slate-800 dark:backdrop-blur-sm dark:shadow-xl dark:shadow-black/40">
              <CardContent className="pt-4 sm:pt-6 px-2 sm:px-6">
                <Trophy className="h-8 w-8 sm:h-12 sm:w-12 text-emerald-600 mx-auto mb-2 sm:mb-4 dark:text-emerald-400" />
                <h3 className="text-lg sm:text-2xl font-bold mb-1 sm:mb-2">Virtuell</h3>
                <p className="text-xs sm:text-base text-gray-600 dark:text-gray-400">Kein echtes Geld</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Im Rampenlicht -- geboostete Markets prominent auf der Landing.
          Identische Komponente wie auf /markets (BoostSpotlight); rendert
          nichts, wenn kein aktiver Boost. */}
      {markets.filter(isMarketBoosted).length > 0 && (
        <section className="py-8 sm:py-12" data-testid="home-spotlight-section">
          <div className="container mx-auto px-4">
            <BoostSpotlight
              markets={markets.filter(isMarketBoosted)}
              outcomes={marketOutcomes}
              navigate={navigate}
            />
          </div>
        </section>
      )}

      {/* Trending Markets */}
      {trending.length > 0 && (
        <section className="py-8 sm:py-12" data-testid="trending-section">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Flame className="h-6 w-6 text-orange-500" />
                <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-700 to-blue-900 dark:from-blue-200 dark:to-white bg-clip-text text-transparent">Trending</h2>
                <span className="text-xs text-gray-400 hidden sm:inline dark:text-gray-500">Letzte 24h</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-emerald-600 hover:text-emerald-700 dark:hover:text-emerald-400 dark:text-emerald-400"
                onClick={() => navigate('/markets')}
                data-testid="trending-see-all"
              >
                Alle anzeigen <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {trending.map((market, i) => (
                <TrendingCard key={market.id} market={market} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Markets */}
      <section className="py-8 sm:py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-6 sm:mb-8 bg-gradient-to-r from-blue-700 to-blue-900 dark:from-blue-200 dark:to-white bg-clip-text text-transparent pb-1">Featured Markets</h2>
          
          {/* Filter row — gleiche Optik wie auf /markets:
              Status-Switch (Open/Settled) links + Kategorie-Pills rechts.
              Bewusst nicht hinter Tabs/Radix-UI versteckt, damit der
              Look 1:1 mit MarketsPage übereinstimmt — auch im
              ausgeloggten Zustand. */}
          <div className="flex items-center gap-3 mb-5">
            <div className="flex-shrink-0">
              <div className="inline-flex p-0.5 bg-white dark:bg-slate-900/60 dark:backdrop-blur-sm border border-gray-200 dark:border-slate-700 rounded-full shadow-sm dark:bg-slate-900/80">
                {[{ key: 'open', label: 'Open' }, { key: 'settled', label: 'Settled' }].map(f => {
                  const isActive = activeStatus === f.key;
                  return (
                    <button
                      key={f.key}
                      onClick={() => setActiveStatus(f.key)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        isActive
                          ? 'bg-blue-900 dark:bg-blue-500/30 text-white'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                      }`}
                      data-testid={`home-status-filter-${f.key}`}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex-shrink-0 w-px h-6 bg-gray-200 dark:bg-slate-700" aria-hidden />

            <div className="flex-1 min-w-0 overflow-x-auto scrollbar-hide">
              <div className="flex gap-2 items-center">
                {categories.map((cat) => {
                  const isActive = (activeCategory || 'all').toLowerCase() === cat.toLowerCase();
                  return (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                        isActive
                          ? 'bg-blue-800 dark:bg-blue-500/30 text-white shadow-sm'
                          : 'bg-white dark:bg-slate-900/60 dark:backdrop-blur-sm text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800/60'
                      }`}
                      data-testid={`home-category-filter-${cat}`}
                    >
                      {cat === 'all' ? 'Alle' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {(() => {
            const matchesStatus = (status) => {
              if (activeStatus === 'settled') return status === 'settled';
              return status !== 'settled' && status !== 'closed';
            };
            const matchesCat = (m, catField) =>
              activeCategory === 'all' || (m[catField] || '').toLowerCase() === activeCategory.toLowerCase();
            const filteredPred = markets.filter(m => matchesCat(m, 'category') && matchesStatus(m.status));
            const filteredCivic = civicMarkets.filter(m => matchesCat(m, 'civic_category') && matchesStatus(m.status));
            // Prediction + Civic in EINER gemischten Liste (Boost oben,
            // dann pro Status-Tier hochvolumige zuerst, Rest gleichmaessig
            // verwoben). Siehe mixMarketList in utils.
            const combined = mixMarketList(filteredPred, filteredCivic);
            return (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {combined.map((item) => (
                    item.type === 'prediction' ? (
                      <MarketCard
                        key={item.data.id}
                        market={item.data}
                        outcomes={marketOutcomes[item.data.id] || []}
                        onBetPlaced={() => refreshMarketOutcomes(item.data.id)}
                      />
                    ) : (
                      <CivicMarketCard key={item.data.id} market={item.data} initialResult={civicResults[item.data.id]} />
                    )
                  ))}
                </div>
                {combined.length === 0 && (
                  <div className="text-center py-12 text-gray-400 dark:text-gray-500">Keine Märkte in dieser Auswahl.</div>
                )}
              </>
            );
          })()}
        </div>
      </section>

      {showPurchaseDialog && (
        <CreditPurchaseDialog
          isOpen={showPurchaseDialog}
          onClose={() => setShowPurchaseDialog(false)}
          onPurchaseSuccess={handlePurchaseSuccess}
        />
      )}
    </div>
  );
};

export default HomePage;
